import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import ad from 'api/axios/foka/ad'
import { createAxiosThunk } from 'utils/reduxUtil'
import { getTrackInfo, linkedTracks } from 'api/axios/crux/bcon'
import { CampaignTypes } from 'features/Codes'
import { fail } from '../../toast/Toast'

const toCommand = ({
  id,
  campaignType,
  bizChannelId,
  name,
  conversionTrackingId,
  traceUrlYn,
  dailyBudgetYn,
  dailyBudgetAmount,
}) => ({
  id,
  campaignType,
  bizChannelId,
  name,
  conversionTrackingId,
  traceUrlYn: traceUrlYn || 'Y',
  dailyBudgetYn: dailyBudgetYn || 'N',
  dailyBudgetAmount: dailyBudgetAmount || '',
  beforeDailyBudgetYn: dailyBudgetYn || 'N',
  beforeDailyBudgetAmount: dailyBudgetAmount || '',
})

export const fetchBizChannels = createAxiosThunk(
  'campaign/fetchBizChannels',
  () => ad.getBizChannels()
)

export const fetchTrackInfo = createAxiosThunk(
  'campaign/fetchTrackInfo',
  (trackId, { getState }) => {
    const adAccountId = getState().account.current.id
    return getTrackInfo({
      trackId,
      adAccountId,
    })
  }
)

export const fetchTrackerRights = createAxiosThunk(
  'campaign/fetchTrackerRights',
  (noArgs, { getState }) => {
    const adAccountId = getState().account.current.id
    return linkedTracks({
      adAccountId,
      page: 0,
      size: 100,
    })
  }
)

export const checkGenerable = createAsyncThunk(
  'campaign/checkGenerable',
  async (noArgs, { getState }) => {
    const [premiumlinkGenerable, talkchannelGenerable] = await Promise.all([
      ad.getCampaignGenerable(CampaignTypes.PREMIUM_LINK),
      ad.getCampaignGenerable(CampaignTypes.TALK_CHANNEL),
    ])
    const generable =
      premiumlinkGenerable?.data.generable ||
      talkchannelGenerable?.data.generable
    if (generable === false) {
      fail(
        '광고계정내 등록 제한된 캠페인수를 초과하였습니다.\n미사용 캠페인을 삭제하세요.'
      )
      const adAccountId = getState().account.current.id
      history.push(`/${adAccountId}/dashboard`)
    }
  }
)

export const createCampaign = createAxiosThunk(
  'campaign/createCampaign',
  (noArgs, { getState }) => {
    const {
      campaignType,
      bizChannelId,
      name,
      conversionTrackingId,
      traceUrlYn,
      dailyBudgetYn,
      dailyBudgetAmount,
    } = getState().ad.campaign.form
    return ad.postCampaign({
      campaignType,
      bizChannelId,
      name,
      conversionTrackingId,
      traceUrlYn,
      dailyBudgetYn,
      dailyBudgetAmount,
    })
  }
)

export const updateCampaign = createAxiosThunk(
  'campaign/updateCampaign',
  (noArgs, { getState }) => {
    const {
      id,
      campaignType,
      bizChannelId,
      name,
      conversionTrackingId,
      traceUrlYn,
      dailyBudgetYn,
      dailyBudgetAmount,
    } = getState().ad.campaign.form
    return ad.putCampaign({
      campaignId: id,
      campaignType,
      bizChannelId,
      name,
      conversionTrackingId,
      traceUrlYn,
      dailyBudgetYn,
      dailyBudgetAmount,
    })
  }
)

export const fetchCampaign = createAxiosThunk(
  'campaign/fetchCampaign',
  campaignId => ad.getCampaign(campaignId)
)

const initialState = {
  isLoading: false,
  form: {
    id: null,
    campaignType: CampaignTypes.PREMIUM_LINK,
    bizChannelId: null,
    name: '',
    conversionTrackingId: null,
    traceUrlYn: 'Y',
    dailyBudgetYn: 'N',
    dailyBudgetAmount: '',
    beforeDailyBudgetYn: 'N',
    beforeDailyBudgetAmount: undefined,
  },
  bizChannel: {
    isLoading: false,
    hasError: false,
    content: [],
  },
  trackerRight: {
    selected: {
      id: undefined,
      name: '',
      deleted: false,
    },
    isLoading: false,
    hasError: false,
    content: [],
  },
}

const campaignSlice = createSlice({
  name: 'campaign',
  initialState,

  reducers: {
    reset: () => initialState,
    setCampaignType: (state, { payload: campaignType }) => {
      state.form.campaignType = campaignType
      state.form.bizChannelId = null
    },
    setName: (state, { payload: name }) => {
      state.form.name = name
    },
    setBizChannelId: (state, { payload: bizChannelId }) => {
      state.form.bizChannelId = bizChannelId
    },
    setConversionTrackingId: (state, { payload: conversionTrackingId }) => {
      state.form.conversionTrackingId = conversionTrackingId
      const selected = state.trackerRight.content.find(
        ({ trackId }) => conversionTrackingId === trackId
      )
      if (selected) {
        state.trackerRight.selected = {
          id: selected.trackId,
          name: selected.trackName,
          deleted: selected.deleted,
        }
      } else {
        state.trackerRight.selected = {
          id: undefined,
          name: '',
          deleted: false,
        }
      }
    },
    setTraceUrlYn: (state, { payload: traceUrlYn }) => {
      state.form.traceUrlYn = traceUrlYn
    },
    setDailyBudgetAmount: (state, { payload: dailyBudgetAmount }) => {
      state.form.dailyBudgetAmount = dailyBudgetAmount
    },
    setDailyBudgetYn: (state, { payload: dailyBudgetYn }) => {
      state.form.dailyBudgetYn = dailyBudgetYn
      if (dailyBudgetYn) {
        state.form.dailyBudgetAmount = ''
      }
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchBizChannels.pending, state => {
      state.bizChannel.isLoading = true
    })

    builder.addCase(fetchBizChannels.fulfilled, (state, { payload }) => {
      state.bizChannel.content = payload.content
      state.bizChannel.isLoading = false
      state.bizChannel.hasError = false
    })

    builder.addCase(fetchBizChannels.rejected, state => {
      state.bizChannel.content = []
      state.bizChannel.isLoading = false
      state.bizChannel.hasError = true
    })

    builder.addCase(fetchTrackInfo.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchTrackInfo.fulfilled,
      (
        state,
        {
          payload: {
            body: { trackId, name, deletedAt },
          },
        }
      ) => {
        state.isLoading = false
        state.trackerRight.selected.id = trackId
        state.trackerRight.selected.name = name
        state.trackerRight.selected.deleted = !!deletedAt
      }
    )

    builder.addCase(fetchTrackInfo.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchTrackerRights.pending, state => {
      state.trackerRight.isLoading = true
    })

    builder.addCase(
      fetchTrackerRights.fulfilled,
      (
        state,
        {
          payload: {
            body: { content },
          },
        }
      ) => {
        state.trackerRight.content = content
        state.trackerRight.isLoading = false
        state.trackerRight.hasError = false
      }
    )

    builder.addCase(fetchTrackerRights.rejected, state => {
      state.trackerRight.content = []
      state.trackerRight.isLoading = false
      state.trackerRight.hasError = true
    })

    builder.addCase(createCampaign.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      createCampaign.fulfilled,
      (state, { payload: { result } }) => {
        state.form = toCommand(result)
        state.isLoading = false
      }
    )

    builder.addCase(createCampaign.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(updateCampaign.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      updateCampaign.fulfilled,
      (state, { payload: { campaign } }) => {
        state.form = toCommand(campaign)
        state.isLoading = false
      }
    )

    builder.addCase(updateCampaign.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(fetchCampaign.pending, state => {
      state.isLoading = true
    })

    builder.addCase(
      fetchCampaign.fulfilled,
      (state, { payload: { campaign } }) => {
        state.form = toCommand(campaign)
        state.isLoading = false
      }
    )

    builder.addCase(fetchCampaign.rejected, state => {
      state.isLoading = false
    })
  },
})
const { actions, reducer } = campaignSlice
export const {
  reset,
  setCampaignType,
  setName,
  setBizChannelId,
  setConversionTrackingId,
  setTraceUrlYn,
  setDailyBudgetAmount,
  setDailyBudgetYn,
} = actions
export default reducer
